<template>
    <section class="relative overflow-hidden border-b border-green-600 bg-gray-200 dark:bg-gray-800">
        <PagesPageHeader
            :breadcrumbs="breadcrumbs"
            title="Code of Ethics"
            description="Ethics Policy for a Permaculture Agarwood Plantation."
            icon="i-fa-legal"
            iconColor="green"
            color="blue"
            :image="true"
            src="/assets/images/sliders/slider-03.webp" />
    </section>

    <section class="bg-white py-10 dark:bg-gray-800">
        <UContainer>
            <div class="flex flex-col md:grid md:grid-cols-1">
                <p class="mb-2 text-xs text-dark-600">
                    At our Permaculture Agarwood Plantation, we recognize that the heart of ethical operation lies in respect and fairness towards all
                    our stakeholders, workers, partners, the environment, the broader agarwood industry, and our staff. This policy serves as a
                    commitment to uphold the highest standards of ethics in all aspects of our business.
                </p>

                <p class="mb-2 text-xs text-dark-600">
                    This ethics policy is more than a document; it reflects our core values and commitment to ethical, fair, and sustainable practices
                    in all our operations. We understand that ethics are not static, and we commit to regularly reviewing and updating our policies to
                    adapt to new challenges and opportunities. Through adherence to these principles, we aim to be a model of ethical excellence in
                    the agarwood industry, contributing positively to our community and the environment while fostering a sustainable and prosperous
                    future for all.
                </p>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Fair and Ethical Treatment of Workers</h2>

                <ul class="mb-2 ml-6 list-disc space-y-2 text-xs text-dark-600">
                    <li>
                        <span class="font-semibold">Rights and Respect:</span> All workers are entitled to fair treatment, respect, and a workplace
                        free from discrimination and harassment. We commit to respecting human rights and providing equal opportunities regardless of
                        race, gender, religion, or background;
                    </li>

                    <li>
                        <span class="font-semibold">Wages and Benefits:</span> We ensure wages meet or exceed the minimum legal standards and are fair
                        relative to the industry and region. Benefits and compensation will be transparently structured and communicated;
                    </li>

                    <li>
                        <span class="font-semibold">Health and Safety:</span> A safe and healthy work environment is a right for all employees. We
                        will provide training, equipment, and protocols to ensure safety and well-being in the workplace.
                    </li>
                </ul>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Ethical Partnerships</h2>

                <ul class="mb-2 ml-6 list-disc space-y-2 text-xs text-dark-600">
                    <li>
                        <span class="font-semibold">Transparency and Integrity:</span> In dealings with partners, we commit to transparency, honesty,
                        and integrity. Contracts and agreements will be fair, clearly written, and uphold the interests of both parties;
                    </li>

                    <li>
                        <span class="font-semibold">Mutual Respect:</span> We recognize the value of our partners and engage in practices that respect
                        their rights and business interests. This includes prompt payments, fair negotiation, and respecting intellectual property
                        rights;
                    </li>

                    <li>
                        <span class="font-semibold">Sustainability Commitment:</span> Partners are chosen based on shared values of sustainability and
                        ethical practices. We strive to collaborate with those who commit to environmentally friendly and socially responsible
                        operations.
                    </li>
                </ul>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Environmental Responsibility</h2>

                <ul class="mb-2 ml-6 list-disc space-y-2 text-xs text-dark-600">
                    <li>
                        <span class="font-semibold">Conservation and Sustainability:</span> Our practices will continually strive for ecological
                        balance, conserving biodiversity, and minimizing environmental impact. This includes reducing waste, managing resources
                        sustainably, and preventing pollution;
                    </li>

                    <li>
                        <span class="font-semibold">Climate Action:</span> We commit to actively reducing our carbon footprint and mitigating climate
                        impacts through innovative and sustainable practices, like renewable energy usage and carbon sequestration efforts;
                    </li>

                    <li>
                        <span class="font-semibold">Responsible Cultivation:</span> Agarwood cultivation and harvesting will be conducted responsibly,
                        ensuring the survival and health of trees, and adhering to any local, national, or international regulations that protect
                        these precious resources.
                    </li>
                </ul>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Commitment to the Agarwood Industry</h2>

                <ul class="mb-2 ml-6 list-disc space-y-2 text-xs text-dark-600">
                    <li>
                        <span class="font-semibold">Industry Standards:</span> We pledge to uphold and exceed the industry standards for ethical and
                        sustainable agarwood production. This includes participating in certifications and audit programs that ensure our practices
                        align with global expectations;
                    </li>

                    <li>
                        <span class="font-semibold">Knowledge Sharing:</span> We believe in contributing to the industry’s growth by sharing
                        knowledge, research, and innovations that can advance sustainable and ethical practices in the agarwood community.
                    </li>
                </ul>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Respect and Development of Our Staff</h2>

                <ul class="mb-2 ml-6 list-disc space-y-2 text-xs text-dark-600">
                    <li>
                        <span class="font-semibold">Professional Growth:</span> We are committed to the professional development of our staff,
                        providing opportunities for training, advancement, and personal growth within the company;
                    </li>

                    <li>
                        <span class="font-semibold">Open Communication:</span> An open-door policy will be maintained where staff can freely express
                        concerns, provide feedback, and contribute ideas to improve operations and policies;
                    </li>

                    <li>
                        <span class="font-semibold">Work-Life Balance:</span> Recognizing the importance of personal well-being, we strive to provide
                        a balanced work environment that respects personal time and family commitments.
                    </li>
                </ul>

                <h2 class="mb-2 text-base font-semibold text-dark-950">Contacting Us</h2>

                <p class="mb-2 text-xs text-dark-600">
                    If you have any questions or concerns we encourage you to contact us using our
                    <NuxtLink
                        to="/corporate/contact-us"
                        class="font-semibold text-dark-950 underline decoration-dashed underline-offset-2 hover:no-underline"
                        >Secure Contact Form</NuxtLink
                    >.
                </p>

                <UDivider class="my-4" />

                <p class="mb-2 text-xs italic text-dark-400">This document was last updated on August 11, 2021</p>
            </div>
        </UContainer>
    </section>
</template>

<script setup>
    definePageMeta({
        layout: 'default'
    })

    const config = useRuntimeConfig()
    const router = useRouter()

    useHead({
        title: `${config.public.company.name} | Code of Ethics`,
        meta: [
            {
                name: 'description',
                content: 'Ethics Policy for a Permaculture Agarwood Plantation.'
            },
            { name: 'robots', content: 'index, follow' }
        ],
        link: [{ rel: 'canonical', href: `${config.public.base.url + router.currentRoute.value.path}` }]
    })

    const breadcrumbs = [{ label: 'Home', to: '/' }, { label: 'Legal', to: '/legal' }, { label: 'Code of Ethics' }]
</script>
